import { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Paperclip, Upload, Download, Trash2, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";

const CATEGORIES = [
  { value: "exame", label: "Exame" },
  { value: "laudo", label: "Laudo" },
  { value: "receita_externa", label: "Receita externa" },
  { value: "imagem", label: "Imagem" },
  { value: "outro", label: "Outro" },
];

const ICON_FOR = (ct = "") => {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct === "application/pdf") return FileText;
  return FileIcon;
};

const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function AttachmentsSection({ patientId, currentUser, initialAttachments = [] }) {
  const [items, setItems] = useState(initialAttachments || []);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("exame");
  const [description, setDescription] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { setItems(initialAttachments || []); }, [initialAttachments]);

  const reload = async () => {
    try {
      const { data } = await api.get(`/patients/${patientId}/attachments`);
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Escolha um arquivo"); return; }
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) { toast.error("Somente JPG, PNG ou PDF"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo excede 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (description) fd.append("description", description);
      await api.post(`/patients/${patientId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Anexo enviado");
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const onDownload = async (att) => {
    try {
      const token = localStorage.getItem("optia_token");
      const res = await fetch(`${BACKEND_URL}/api/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Falha ao baixar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "anexo";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "Falha ao baixar"); }
  };

  const onDelete = async (att) => {
    if (!window.confirm(`Excluir "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Anexo excluído");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = (att) =>
    currentUser?.role === "admin" ||
    currentUser?.id === att.uploaded_by ||
    currentUser?.id === att.patient_id;

  return (
    <Card className="card-soft mt-5" data-testid="chart-attachments">
      <CardContent className="p-6">
        <h2 className="font-display text-lg font-semibold text-navy-900 flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-teal-600" />Anexos (exames, laudos, imagens)
        </h2>

        <form onSubmit={onUpload} className="mt-4 grid gap-3 sm:grid-cols-[1fr_170px_140px] items-end" data-testid="attachment-upload-form">
          <div>
            <label className="text-xs text-slate-500">Arquivo (JPG, PNG ou PDF — até 10 MB)</label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="mt-1 block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700 file:text-sm hover:file:bg-teal-100 cursor-pointer"
              data-testid="attachment-file-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Categoria</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white"
              data-testid="attachment-category-select"
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <Button type="submit" disabled={uploading} className="btn-primary" data-testid="attachment-upload-btn">
            <Upload className="w-4 h-4 mr-1" />{uploading ? "Enviando…" : "Enviar"}
          </Button>
          <div className="sm:col-span-3">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição (opcional) — ex: Hemograma completo 15/03/2026"
              data-testid="attachment-description-input"
            />
          </div>
        </form>

        <div className="mt-6 space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum anexo até o momento.</p>
          ) : (
            items.map((att) => {
              const Icon = ICON_FOR(att.content_type);
              return (
                <div
                  key={att.id}
                  className="flex items-center gap-3 border border-slate-200 rounded-xl p-3 hover:border-teal-300 transition"
                  data-testid={`attachment-item-${att.id}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-teal-50 grid place-items-center text-teal-700 shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-navy-900 truncate">{att.original_filename}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                      <span className="capitalize">{att.category}</span>
                      <span>·</span>
                      <span>{fmtSize(att.size)}</span>
                      <span>·</span>
                      <span>{fmtDate(att.created_at)}</span>
                      {att.uploaded_by_name && <>
                        <span>·</span>
                        <span>por {att.uploaded_by_name}</span>
                      </>}
                    </div>
                    {att.description && <div className="text-xs text-slate-600 mt-0.5 truncate">{att.description}</div>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onDownload(att)} data-testid={`attachment-download-${att.id}`}>
                    <Download className="w-3 h-3 mr-1" />Baixar
                  </Button>
                  {canDelete(att) && (
                    <Button size="sm" variant="ghost" onClick={() => onDelete(att)} className="text-red-600 hover:bg-red-50" data-testid={`attachment-delete-${att.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <p className="text-xs text-slate-400 mt-4">
          Anexos são armazenados de forma privada. Todo acesso é auditado.
        </p>
      </CardContent>
    </Card>
  );
}
